// The one table, the row menu and the confirmation (02-DESIGN/03-console/
// 01-the-console.md § conventions: a table; add; a row's actions; remove;
// § page anatomy). Every list in the console is this table: it sorts on a
// click of a sortable header — ascending, then descending, an arrow on the
// sorted column only — narrows by one *Filter* field over the text of every
// shown column and one *Status: any* / *Kind: any* menu per state or kind
// column, says *No rows match* with *Clear filters* when nothing is left,
// opens a row's detail on a click anywhere on it, and puts everything else a
// person can do to the row behind one three-dot row menu at its right end,
// the removes last after a divider in the danger colour, each asked first.
// It never scrolls sideways: a column marked `narrow: false` is dropped from
// the narrow layout. The page's one primary action stays in its PageHead.

import { ArrowDown, ArrowUp, Ellipsis } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "./components.tsx";
import { ShowMore } from "./more.tsx";
import {
  facetOptions,
  isSortable,
  nextSort,
  orderMenu,
  shownRows,
  type Facets,
  type Sort,
  type Sortable,
} from "./rows.ts";
import "./table.css";

export type { Sort } from "./rows.ts";

/** Column is one column of a table: its header, its width, how a cell draws, and what it sorts and filters by. */
export interface Column<R> extends Sortable<R> {
  /** The header's words. */
  header: string;
  /** The grid track: `minmax(0, 1fr)` where omitted. */
  width?: string;
  /** What the cell draws: no link and no button in it, except a one-time value's copy button. */
  cell: (r: R) => ReactNode;
  /** The words a facet menu shows for one value; the value itself where omitted. */
  facetLabel?: (v: string) => string;
  /** False drops the column from the narrow layout; its value is on the row's detail. */
  narrow?: boolean;
  /** "end" sets the cell's content at the row's right end. */
  align?: "end";
  /**
   * A cell drawn once per run: where the row above in the order shown has the
   * same key, the cell is left empty — a record's time once per minute.
   */
  run?: (r: R) => string;
}

/** Facet is a menu over a value the rows hold but no column draws: *Family: any*. */
export interface Facet<R> {
  key: string;
  /** The menu's label: *Family*. */
  facet: string;
  value: (r: R) => string;
  /** The words a menu shows for one value; the value itself where omitted. */
  facetLabel?: (v: string) => string;
}

/** Ask is a remove's confirmation: the thing named, and what removing it does in the person's words. */
export interface Ask {
  /** The question, naming the thing: *Remove model gpt-review?* */
  title: string;
  /** What it does: *Workloads that call it will get not allowed until you add one under the same name.* */
  consequence: string;
  /** The confirming button's words; the entry's label where omitted. */
  verb?: string;
}

/**
 * MenuEntry is one thing a person can do to a row, in the design's words. A
 * remove — `danger: true` — is drawn last after a divider in the danger
 * colour and always asks first; any other entry may ask too.
 */
export type MenuEntry =
  | { label: string; onSelect: () => void; danger?: false; confirm?: Ask }
  | { label: string; onSelect: () => void; danger: true; confirm: Ask };

/**
 * Table is every list in the console. `rows` are what the page read; the
 * table sorts and filters them in the browser. `to` or `onOpen` opens a row's
 * page on a click anywhere on it; `menu` gives
 * a row's other actions, and a row whose menu is empty has no button. `mark`
 * names a row just added, marked for a moment; `current` the row whose detail
 * is open beside the list.
 */
export function Table<R>({
  label,
  rows,
  columns,
  rowKey,
  sort: initial,
  to,
  linkState,
  onOpen,
  menu,
  mark,
  current,
  facets: extra = [],
  top,
}: {
  /** What the table lists, for a screen reader and the filter's name: *Models*. */
  label: string;
  rows: readonly R[];
  columns: readonly Column<R>[];
  rowKey: (r: R) => string;
  /** The sort the table opens with: newest first for records, by name for things. */
  sort: Sort;
  /** The row's page; a row it answers no page for does not open. */
  to?: (r: R) => string | undefined;
  /** What the row's page is told of where it was opened from, in the navigation's state; never the address. */
  linkState?: unknown;
  onOpen?: (r: R) => void;
  menu?: (r: R) => MenuEntry[];
  mark?: string;
  current?: string;
  /** Menus over values no column draws, beside the columns' own. */
  facets?: readonly Facet<R>[];
  /** Draws the first `top` rows shown, with *Show more* for the rest. */
  top?: number;
}) {
  const [sort, setSort] = useState<Sort>(initial);
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState<Facets>({});
  const [all, setAll] = useState(false);
  const marked = useMarked(mark);
  const navigate = useNavigate();

  // A menu over a value no column draws narrows as a column's does, and adds no text to the filter.
  const quiet = extra.map((f) => ({ ...f, text: () => "" }));
  const menus: readonly (Sortable<R> & { facetLabel?: (v: string) => string })[] = [
    ...columns.filter((c) => c.facet !== undefined),
    ...quiet,
  ];
  const kept = shownRows(rows, [...columns, ...quiet], { query, facets, sort });
  const shown = top === undefined || all ? kept : kept.slice(0, top);
  const left = kept.length - shown.length;
  const filtered = query !== "" || Object.keys(facets).length > 0;
  const withMenu = menu !== undefined;
  const tracks = columns.map((c) => c.width ?? "minmax(0, 1fr)");
  const narrowTracks = columns.filter((c) => c.narrow !== false).map((c) => c.width ?? "minmax(0, 1fr)");
  const style = {
    "--cols": [...tracks, ...(withMenu ? ["36px"] : [])].join(" "),
    "--cols-narrow": [...narrowTracks, ...(withMenu ? ["36px"] : [])].join(" "),
  } as CSSProperties;

  const open = (r: R) => {
    const path = to?.(r);
    if (path !== undefined) {
      void navigate(path, linkState === undefined ? undefined : { state: linkState });
    } else if (to === undefined && onOpen !== undefined) {
      onOpen(r);
    }
  };
  const clear = () => {
    setQuery("");
    setFacets({});
  };

  return (
    <div className="dtable-wrap">
      <div className="dtable-bar">
        <input
          className="input dtable-filter"
          type="search"
          placeholder="Filter"
          aria-label={`Filter ${label.toLocaleLowerCase()}`}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          autoComplete="off"
          spellCheck={false}
        />
        {menus.map((c) => {
          const facet = c.facet ?? "";
          const options = facetOptions(rows, c);
          return (
            <select
              key={c.key}
              className="input dtable-facet"
              aria-label={facet}
              value={facets[c.key] ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setFacets((f) => {
                  const next = Object.entries(f).filter(([k]) => k !== c.key);
                  return Object.fromEntries(v === "" ? next : [...next, [c.key, v] as const]);
                });
              }}
            >
              <option value="">{`${facet}: any`}</option>
              {options.map((o) => (
                <option key={o} value={o}>
                  {`${facet}: ${c.facetLabel !== undefined ? c.facetLabel(o) : o}`}
                </option>
              ))}
            </select>
          );
        })}
      </div>
      <div className="dtable" role="table" aria-label={label} aria-rowcount={shown.length + 1}>
        <div role="rowgroup">
          <div className="dt-tr dt-th" role="row" style={style}>
            {columns.map((c) => {
              const sorted = sort.key === c.key;
              const cls = [c.narrow === false ? "dt-wide" : "", c.align === "end" ? "dt-end" : ""]
                .filter((x) => x !== "")
                .join(" ");
              return (
                <span
                  key={c.key}
                  role="columnheader"
                  className={cls === "" ? undefined : cls}
                  aria-sort={sorted ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
                >
                  {isSortable(c) ? (
                    <button
                      type="button"
                      className="dt-sort"
                      onClick={() => {
                        setSort((s) => nextSort(s, c.key));
                      }}
                    >
                      {c.header}
                      {sorted &&
                        (sort.dir === "asc" ? (
                          <ArrowUp size={12} strokeWidth={1.75} aria-hidden="true" />
                        ) : (
                          <ArrowDown size={12} strokeWidth={1.75} aria-hidden="true" />
                        ))}
                    </button>
                  ) : (
                    c.header
                  )}
                </span>
              );
            })}
            {withMenu && (
              <span role="columnheader" className="dt-end">
                <span className="visually-hidden">Actions</span>
              </span>
            )}
          </div>
        </div>
        <div role="rowgroup">
          {shown.length === 0 && (
            <div className="dt-none" role="row">
              <span role="cell">
                No rows match.{" "}
                {filtered && (
                  <button type="button" className="text-link dt-clear" onClick={clear}>
                    Clear filters
                  </button>
                )}
              </span>
            </div>
          )}
          {shown.map((r, at) => {
            const key = rowKey(r);
            const above = at > 0 ? shown[at - 1] : undefined;
            const path = to?.(r);
            const opensRow = path !== undefined || (to === undefined && onOpen !== undefined);
            const entries = menu !== undefined ? menu(r) : [];
            const cls = [
              "dt-tr",
              "dt-row",
              opensRow ? "dt-opens" : "",
              marked !== undefined && marked === key ? "dt-marked" : "",
            ]
              .filter((x) => x !== "")
              .join(" ");
            return (
              <div
                key={key}
                className={cls}
                role="row"
                style={style}
                aria-current={current === key ? "true" : undefined}
                onClick={
                  opensRow
                    ? (e: ReactMouseEvent<HTMLDivElement>) => {
                        if (fromControl(e.target, e.currentTarget)) {
                          return;
                        }
                        open(r);
                      }
                    : undefined
                }
              >
                {columns.map((c, i) => {
                  const repeated = c.run !== undefined && above !== undefined && c.run(above) === c.run(r);
                  const body = repeated ? null : c.cell(r);
                  const cellCls = [
                    "dt-cell",
                    c.narrow === false ? "dt-wide" : "",
                    c.align === "end" ? "dt-end" : "",
                  ]
                    .filter((x) => x !== "")
                    .join(" ");
                  return (
                    <span key={c.key} role="cell" className={cellCls}>
                      {i === 0 && path !== undefined ? (
                        <Link className="dt-open" to={path} state={linkState}>
                          {body}
                        </Link>
                      ) : i === 0 && to === undefined && onOpen !== undefined ? (
                        <button
                          type="button"
                          className="dt-open"
                          onClick={() => {
                            onOpen(r);
                          }}
                        >
                          {body}
                        </button>
                      ) : (
                        body
                      )}
                    </span>
                  );
                })}
                {withMenu && (
                  <span role="cell" className="dt-cell dt-end">
                    <RowMenu label={`Actions for ${textOfRow(columns, r)}`} entries={entries} />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {top !== undefined && kept.length > top && <ShowMore open={all} onToggle={setAll} left={left} />}
    </div>
  );
}

/** textOfRow names a row for its menu's accessible name: its first column's value. */
export function textOfRow<R>(columns: readonly Column<R>[], r: R): string {
  const first = columns[0];
  if (first === undefined) {
    return "this row";
  }
  // A value that is a word names the row; one that only orders it — a
  // number, a rank — does not, and the column's words do (hq issue 177).
  const v = first.value?.(r);
  if (typeof v === "string" && v !== "") {
    return v;
  }
  const t = first.text?.(r);
  if (t !== undefined && t !== "") {
    return t;
  }
  return "this row";
}

/** fromControl says whether a click landed on a control of its own — a link, a button, a field, the menu. */
function fromControl(target: EventTarget, row: Element): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const hit = target.closest("a, button, input, select, textarea, label, .dt-menu");
  return hit !== null && row.contains(hit);
}

/** useMarked is the row just added, for a moment: the key while it is fresh, then nothing. */
function useMarked(mark: string | undefined): string | undefined {
  const [faded, setFaded] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (mark === undefined) {
      return;
    }
    const t = setTimeout(() => {
      setFaded(mark);
    }, 4000);
    return () => {
      clearTimeout(t);
    };
  }, [mark]);
  return mark !== undefined && faded !== mark ? mark : undefined;
}

/**
 * RowMenu is the three-dot button at a row's right end and the menu it opens:
 * the row's actions in the design's words, the removes last after a divider
 * in the danger colour, each asked first. With no entries it draws nothing.
 * The arrow keys move through the entries, Escape closes and returns focus.
 * A detail page uses it too, where the design puts actions *in its menu*.
 */
export function RowMenu({ label, entries }: { label: string; entries: readonly MenuEntry[] }) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<MenuEntry | undefined>(undefined);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) {
      return;
    }
    list.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (t instanceof Node && !list.current?.contains(t) && !button.current?.contains(t)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const { rest, removes } = orderMenu(entries);
  if (rest.length === 0 && removes.length === 0) {
    return null;
  }

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const choose = (e: MenuEntry) => {
    setOpen(false);
    if (e.confirm !== undefined) {
      setAsking(e);
      return;
    }
    button.current?.focus();
    e.onSelect();
  };
  const onKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const items = [...(list.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const at = items.indexOf(document.activeElement as HTMLButtonElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === "ArrowDown") {
      go(at + 1);
    } else if (e.key === "ArrowUp") {
      go(at - 1);
    } else if (e.key === "Home") {
      go(0);
    } else if (e.key === "End") {
      go(items.length - 1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };
  const item = (e: MenuEntry) => (
    <button
      key={e.label}
      type="button"
      role="menuitem"
      tabIndex={-1}
      className={e.danger === true ? "dt-item danger" : "dt-item"}
      onClick={() => {
        choose(e);
      }}
    >
      {e.label}
    </button>
  );

  return (
    <span className="dt-menu">
      <button
        ref={button}
        type="button"
        className="btn icon dt-dots"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <Ellipsis size={16} strokeWidth={1.5} aria-hidden="true" />
      </button>
      {open && (
        <div ref={list} id={id} className="dt-popup" role="menu" aria-label={label} onKeyDown={onKey}>
          {rest.map(item)}
          {rest.length > 0 && removes.length > 0 && <div className="dt-divider" role="separator" />}
          {removes.map(item)}
        </div>
      )}
      {asking?.confirm !== undefined && (
        <ConfirmDialog
          ask={asking.confirm}
          verb={asking.confirm.verb ?? asking.label}
          danger={asking.danger === true}
          onConfirm={() => {
            const e = asking;
            setAsking(undefined);
            button.current?.focus();
            e.onSelect();
          }}
          onCancel={() => {
            setAsking(undefined);
            button.current?.focus();
          }}
        />
      )}
    </span>
  );
}

/**
 * ConfirmDialog is the confirmation of § page anatomy: an act that cannot be
 * undone offered a second time, naming the thing and saying what it does in
 * the person's words. *Keep* has the focus; Escape and the scrim keep.
 */
export function ConfirmDialog({
  ask,
  verb,
  danger = true,
  onConfirm,
  onCancel,
  children,
}: {
  ask: Ask;
  verb: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** What the act does, at more length than one sentence: drawn under the consequence. */
  children?: ReactNode;
}) {
  const title = useId();
  const text = useId();
  const keep = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    keep.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
    };
  }, [onCancel]);
  const trap = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") {
      return;
    }
    const items = [...(box.current?.querySelectorAll<HTMLButtonElement>("button") ?? [])];
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };
  return (
    <>
      <div className="scrim dt-scrim" onClick={onCancel} aria-hidden="true" />
      <div
        ref={box}
        className="dt-ask"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={title}
        aria-describedby={text}
        onKeyDown={trap}
      >
        <h2 id={title}>{ask.title}</h2>
        <p id={text}>{ask.consequence}</p>
        {children !== undefined && <div className="dt-ask-more">{children}</div>}
        <div className="dt-ask-foot">
          <button ref={keep} type="button" className="btn quiet" onClick={onCancel}>
            Keep
          </button>
          <Button kind={danger ? "danger" : "primary"} onClick={onConfirm}>
            {verb}
          </Button>
        </div>
      </div>
    </>
  );
}
